import { createSharedComposable } from '@vueuse/core'
import { reactive } from 'vue'
import { useActivityStatus } from './params/activity-status'
import { useCompareCountries } from './params/compare-countries'
import { useCompareCountry } from './params/compare-country'
import { useCompareDimension } from './params/compare-dimension'
import { useCompareRegions } from './params/compare-regions'
import { useCountry } from './params/country'
import { useCountryCard } from './params/country-card'
import { useDimension } from './params/dimension'
import { useFilter } from './params/filter'
import { useImportProducts } from './params/import-products'
import { useImportsByProduct } from './params/imports-by-product'
import { useIndicatorStatus } from './params/indicator-status'
import { useMode } from './params/mode'
import { usePrevalenceUnit } from './params/prevalence-unit'
import { useRegionalView } from './params/regional-view'
import { useRouter } from '../useRouter'
import { useShowFramework } from './params/show-framework'
import { useShowRegions } from './params/show-regions'
import { useSort } from './params/sort'
import { useViewMode } from './params/view-mode'

export type Config = ReturnType<typeof useParams>['config']

/**
 * Use a global chart configuration which includes
 * all the filters, sorting and dimensions options.
 */
export const useParams = createSharedComposable(() => {
    /**
     * Use the current router.
     */
    const { pause, resume } = useRouter()

    /**
     * Set a new config entirely.
     */
    function set(newConfig?: Partial<Config>, replace?: boolean) {
        pause()
        for (const key in config) {
            const value = newConfig?.[key as keyof Config]

            if (value !== undefined || !replace) {
                // @ts-expect-error fine
                config[key] = value
            }
        }
        resume()
    }

    const dimension = useDimension()
    const [compareDimension, compareDimensions] = useCompareDimension()
    const showFramework = useShowFramework()
    const [country, countries] = useCountry()
    const [mode, modes] = useMode()
    const showRegions = useShowRegions()
    const [activeCompareCountries, compareCountries] = useCompareCountries()
    const [compareCountry] = useCompareCountry()
    const [activeCompareRegions, compareRegions] = useCompareRegions()
    const [filter, filters] = useFilter()
    const [sort, sorts] = useSort()
    const [viewMode, viewModes] = useViewMode()
    const [prevalenceUnit, prevalenceUnits] = usePrevalenceUnit()
    const [activityStatus, activityStatuses] = useActivityStatus()
    const [indicatorStatus, indicatorStatuses] = useIndicatorStatus()
    const regionalView = useRegionalView()
    const [activeImportProducts, importProducts] = useImportProducts()
    const countryCard = useCountryCard()
    const importsByProduct = useImportsByProduct()

    const config = reactive({
        country,
        mode,
        compareCountries: activeCompareCountries,
        compareRegions: activeCompareRegions,
        showRegions,
        filter,
        sort,
        viewMode,
        prevalenceUnit,
        activityStatus,
        indicatorStatus,
        dimension,
        compareDimension,
        showFramework,
        compareCountry,
        regionalView,
        countryCard,
        importProducts: activeImportProducts,
        importsByProduct
    })

    const options = reactive({
        compareCountries,
        countries,
        compareRegions,
        filters,
        sorts,
        viewModes,
        prevalenceUnits,
        activityStatuses,
        indicatorStatuses,
        compareDimensions,
        importProducts,
        modes
    })

    return { config, options, set }
})
